import { useEffect, useMemo, useRef, useState } from "react";
import { assignFormValues } from "../../../Utils/Auth";
import {
  getICDPanelsIndex,
  getSpecimenSourceIndex,
  panelsArrItemAddChild,
  panelsArrItemRemoval,
  panelsArrItemRemovalChild,
  panelsArrMakerToSend,
} from "../../../Utils/Common/Requisition";
import useLang from "../../hooks/useLanguage";
import { useLocation } from "react-router-dom";

const MultipleHeaderSelectable = (props: any) => {
  const { t } = useLang();
  const [panelsArrToSend, setPanelsArrToSend] = useState<any>([]);
  const [mutualExclusivityError, setMutualExclusivityError] = useState<string>("");
  const location = useLocation()

  // Mutual exclusivity: builds a Set of panelIDs that must be disabled because another panel
  // in the same panelCombinations group (minimumSelection === 1) is already selected.
  // panelCombinationJson is a JSON string array of { PanelId } objects from the field config.
  const disabledPanelIds = useMemo(() => {
    const disabled = new Set<string>();
    if (!props?.panelCombinations?.length) return disabled;
    props.panelCombinations.forEach((combo: any) => {
      if (combo.minimumSelection !== 1) return;
      let groupPanels: any[] = [];
      try { groupPanels = JSON.parse(combo.panelCombinationJson || "[]"); } catch { return; }
      const groupPanelIds = groupPanels.map((p: any) => String(p.PanelId));
      const selectedInGroup = (props?.panels || []).filter(
        (p: any) => groupPanelIds.includes(String(p.panelID)) && p.isSelected
      );
      if (selectedInGroup.length > 0) {
        groupPanelIds.forEach((id: string) => {
          if (!selectedInGroup.some((p: any) => String(p.panelID) === id)) disabled.add(id);
        });
      }
    });
    return disabled;
  }, [props?.panels, props?.panelCombinations]);

  // Returns the name of the already-selected panel that conflicts with the given panelID,
  // used to build a descriptive error message for the user.
  const getConflictingPanelName = (panelID: string | number): string => {
    if (!props?.panelCombinations?.length) return "";
    for (const combo of props.panelCombinations) {
      if (combo.minimumSelection !== 1) continue;
      let groupPanels: any[] = [];
      try { groupPanels = JSON.parse(combo.panelCombinationJson || "[]"); } catch { continue; }
      const groupPanelIds = groupPanels.map((p: any) => String(p.PanelId));
      if (!groupPanelIds.includes(String(panelID))) continue;
      const conflict = (props?.panels || []).find(
        (p: any) => groupPanelIds.includes(String(p.panelID)) && p.isSelected
      );
      if (conflict) return conflict.panelName;
    }
    return "";
  };
  useEffect(() => {
    const {
      Inputs,
      index,
      fieldIndex,
      depControlIndex,
      isDependency,
      repeatFieldSection,
      isDependencyRepeatFields,
      repeatFieldIndex,
      repeatDependencySectionIndex,
      repeatDepFieldIndex,
      setInputs,
      infectiousData,
      ArrayReqId,
      setInfectiousData,
    } = props;

    const currentField = Inputs?.[index]?.fields?.[fieldIndex];
    let inputsCopy = JSON?.parse(JSON?.stringify(props?.Inputs));
    let specimenSourceIndex = getSpecimenSourceIndex(inputsCopy);

    if (!currentField?.panels || location.state?.reqId) return;

    const specimenInputs = specimenSourceIndex != null ? props?.Inputs[specimenSourceIndex]?.fields : null;

    currentField.panels.forEach((panel: any) => {
      if (!panel.isDefaultSelectOnRequisition) return;

      panel.isSelected = true;

      // Set visibility on matching specimenSources
      if (specimenInputs) {
        specimenInputs?.forEach((field: any) => {
          field?.specimenSources?.forEach((source: any) => {
            if (source?.panelName === panel.panelName) {
              source.isVisible = true;
              source.isSelected = true;
              source?.specimenSourceOption?.forEach((element: any) => {
                element.isSelected = true;
              });
            }
          });
        });
      }

      // Select all test options
      panel.testOptions?.forEach((option: any) => {
        option.isSelected = true;
      });
    });

    const newInputsPromise = assignFormValues(
      Inputs,
      index,
      depControlIndex,
      fieldIndex,
      currentField?.panels,
      isDependency,
      repeatFieldSection,
      isDependencyRepeatFields,
      repeatFieldIndex,
      repeatDependencySectionIndex,
      repeatDepFieldIndex,
      undefined,
      setInputs
    );

    newInputsPromise?.then((updatedSections) => {
      if (!updatedSections) return;

      const infectiousDataCopy = JSON.parse(JSON.stringify(infectiousData));
      const sectionIndex = FindIndex(infectiousData, ArrayReqId);

      if (sectionIndex !== -1) {
        infectiousDataCopy[sectionIndex].sections = updatedSections;
        setInfectiousData([...infectiousDataCopy]);
      }
    });
  }, [props?.IsSelectedByDefaultCompendiumData]);

  useEffect(() => {
    setpanelsArrToSendForEdit();
  }, [props.panels]);

  useEffect(() => {
    setMutualExclusivityError("");
  }, [props?.panels]);

  function FindIndex(arr: any[], rid: any) {
    return arr.findIndex((i: any) => i.reqId === rid);
  }
  const handleChangeParent = (
    panelID: number,
    panelName: string,
    checked: boolean,
    index: number
  ) => {
    // Block selection if this panel is mutually exclusive with an already-selected panel.
    // The user sees a friendly message instead of a silent no-op.
    if (checked && disabledPanelIds.has(String(panelID))) {
      const conflictingName = getConflictingPanelName(panelID);
      setMutualExclusivityError(
        conflictingName
          ? `"${panelName}" cannot be selected because "${conflictingName}" is already selected. Only one panel can be selected from this group.`
          : "Only one panel can be selected from this group. Please deselect the current selection first."
      );
      return;
    }
    setMutualExclusivityError("");
    props.fields.enableRule = "";
    const name = panelName;
    let panelsArrToAppendCopy = [...panelsArrToSend];
    let inputsCopy = JSON?.parse(JSON?.stringify(props?.Inputs));
    if (checked) {
      let ICDPanelIndex = getICDPanelsIndex(props?.Inputs);
      let specimenSourceIndex = getSpecimenSourceIndex(inputsCopy);
      props.Inputs[props.index].fields[props?.fieldIndex].panels[
        index
      ].isSelected = true;
      props.Inputs[props.index].fields[props?.fieldIndex].panels[
        index
      ].testOptions.forEach((testOptions: any) => {
        testOptions.isSelected = true;
      });
      if (ICDPanelIndex) {
        props.Inputs[ICDPanelIndex]?.fields?.forEach((IcdPanelsFields: any) => {
          if (IcdPanelsFields.systemFieldName == "ICDPanels") {
            IcdPanelsFields?.panels?.forEach((panelsInfo: any) => {
              if (panelsInfo.panelName == name) {
                panelsInfo.isVisible = true;
              }
            });
          }
        });
      }
      if (specimenSourceIndex) {
        props?.Inputs[specimenSourceIndex]?.fields?.forEach(
          (specimenSourceFields: any) => {
            specimenSourceFields?.specimenSources?.forEach(
              (panelsInfo: any) => {
                if (panelsInfo?.panelName === name) {
                  panelsInfo.isVisible = true;
                }
              }
            );
          }
        );
      }
      let finalisedPanelsArrToSend = panelsArrMakerToSend(
        index,
        props?.Inputs[props.index]?.fields[props?.fieldIndex]?.panels[index],
        panelsArrToAppendCopy,
        checked
      );
      setPanelsArrToSend(finalisedPanelsArrToSend);
      let newInputs = assignFormValues(
        props?.Inputs,
        props?.index,
        props?.depControlIndex,
        props?.fieldIndex,
        finalisedPanelsArrToSend,
        props?.isDependency,
        props?.repeatFieldSection,
        props?.isDependencyRepeatFields,
        props?.repeatFieldIndex,
        props?.repeatDependencySectionIndex,
        props?.repeatDepFieldIndex,
        undefined,
        props?.setInputs
      );
      newInputs?.then((res) => {
        let infectiousDataCopy = JSON?.parse(
          JSON?.stringify(props?.infectiousData)
        );
        infectiousDataCopy[
          FindIndex(props?.infectiousData, props?.ArrayReqId)
        ].sections = res;
        props?.setInfectiousData([...infectiousDataCopy]);
      });
    }
    if (!checked) {
      // debugger;
      let ICDPanelIndex = getICDPanelsIndex(props?.Inputs);
      let specimenSourceIndex = getSpecimenSourceIndex(inputsCopy);
      if (ICDPanelIndex !== undefined && ICDPanelIndex !== null) {
        inputsCopy[ICDPanelIndex]?.fields?.forEach((IcdPanelsFields: any) => {
          if (IcdPanelsFields.systemFieldName == "ICDPanels") {
            IcdPanelsFields?.panels?.forEach((panelsInfo: any) => {
              if (panelsInfo.panelName == name) {
                panelsInfo.isVisible = false; // Properly setting isVisible to false
              }
            });
          }
        });
      }
      if (specimenSourceIndex) {
        inputsCopy[specimenSourceIndex]?.fields?.forEach(
          (specimenSourceFields: any) => {
            specimenSourceFields?.specimenSources?.forEach(
              (panelsInfo: any) => {
                if (panelsInfo?.panelName === name) {
                  panelsInfo.isVisible = false;
                }
              }
            );
          }
        );
      }
      inputsCopy[props.index].fields[props?.fieldIndex].panels[
        index
      ].isSelected = false;
      inputsCopy[props.index].fields[props?.fieldIndex].panels[
        index
      ].testOptions.forEach((testOptions: any) => {
        testOptions.isSelected = false;
      });

      let finalisedPanelsArrToSend = panelsArrItemRemoval(
        panelID,
        panelsArrToAppendCopy
      );
      setPanelsArrToSend(finalisedPanelsArrToSend);
      let newInputs = assignFormValues(
        inputsCopy,
        props?.index,
        props?.depControlIndex,
        props?.fieldIndex,
        finalisedPanelsArrToSend,
        props?.isDependency,
        props?.repeatFieldSection,
        props?.isDependencyRepeatFields,
        props?.repeatFieldIndex,
        props?.repeatDependencySectionIndex,
        props?.repeatDepFieldIndex,
        undefined,
        props?.setInputs
      );
      newInputs?.then((res) => {
        let infectiousDataCopy = JSON?.parse(
          JSON?.stringify(props?.infectiousData)
        );
        infectiousDataCopy[
          FindIndex(props?.infectiousData, props?.ArrayReqId)
        ].sections = res;
        props?.setInfectiousData([...infectiousDataCopy]);
      });
    }
  };
  const [storeChild, setStoreChild] = useState<any>([]);
  const handleChangeChild = (
    testID: string,
    checked: boolean,
    parentPanelName: string,
    index: number,
    childIndex: number,
    panelID: any
  ) => {
    props.fields.enableRule = "";
    let inputsCopy = JSON.parse(JSON?.stringify(props?.Inputs));
    let panelsArrToAppendCopy = JSON.parse(JSON?.stringify(panelsArrToSend));
    let panelsCopy = [...props?.panels];
    let ICDPanelIndex = getICDPanelsIndex(inputsCopy);
    if (checked) {
      let specimenSourceIndex = getSpecimenSourceIndex(inputsCopy);
      props.Inputs[ICDPanelIndex]?.fields?.forEach((IcdPanelsFields: any) => {
        if (IcdPanelsFields.systemFieldName == "ICDPanels") {
          IcdPanelsFields?.panels?.forEach((panelsInfo: any) => {
            if (panelsInfo.panelName == parentPanelName) {
              panelsInfo.isVisible = true;
            }
          });
        }
      });
      if (specimenSourceIndex) {
        inputsCopy[specimenSourceIndex]?.fields?.forEach(
          (specimenSourceFields: any) => {
            specimenSourceFields?.specimenSources?.forEach(
              (panelsInfo: any) => {
                if (panelsInfo?.panelName === parentPanelName) {
                  panelsInfo.isVisible = true;
                }
              }
            );
          }
        );
      }
      // Update child checkbox state
      inputsCopy[props.index].fields[props?.fieldIndex].panels[
        index
      ].testOptions[childIndex].isSelected = true;
      // Update parent checkbox state
      inputsCopy[props.index].fields[props?.fieldIndex].panels[
        index
      ].isSelected = true;
      const currentPanel =
        inputsCopy[props.index].fields[props?.fieldIndex].panels[index];
      console.log(currentPanel, "currentPanel");
      // Store the selected panel in storeChild if not already present
      setStoreChild((prevStoreChild: any) => {
        const panelIndex = prevStoreChild.findIndex(
          (panel: any) => panel.panelName === currentPanel.panelName
        );
        if (panelIndex !== -1) {
          // Update the existing panel with the recent one
          const updatedStoreChild = [...prevStoreChild];
          updatedStoreChild[panelIndex] = currentPanel;
          return updatedStoreChild;
        }
        // Add the new panel if it doesn't exist
        return [...prevStoreChild, currentPanel];
      });
      const allChildUnchecked = inputsCopy[props.index].fields[
        props?.fieldIndex
      ].panels[index].testOptions.every((option: any) => !option.isSelected);
      // If all child checkboxes are unchecked, update parent checkbox state and panel visibility
      if (allChildUnchecked) {
        // Uncheck the parent checkbox
        inputsCopy[props.index].fields[props?.fieldIndex].panels[
          index
        ].isSelected = false;
        // Update parent panel visibility
      }

      let newInputs = assignFormValues(
        inputsCopy,
        // props?.dependenceyControls,
        props?.index,
        props?.depControlIndex,
        props?.fieldIndex,
        storeChild,
        props?.isDependency,
        props?.repeatFieldSection,
        props?.isDependencyRepeatFields,
        props?.repeatFieldIndex,
        props?.repeatDependencySectionIndex,
        props?.repeatDepFieldIndex,
        undefined,
        props?.setInputs
      );
      newInputs?.then((res) => {
        let infectiousDataCopy = JSON?.parse(
          JSON?.stringify(props?.infectiousData)
        );
        infectiousDataCopy[
          FindIndex(props?.infectiousData, props?.ArrayReqId)
        ].sections = res;
        props?.setInfectiousData([...infectiousDataCopy]);
      });
    } else {
      let specimenSourceIndex = getSpecimenSourceIndex(inputsCopy);
      props.Inputs[ICDPanelIndex]?.fields?.forEach((IcdPanelsFields: any) => {
        if (IcdPanelsFields.systemFieldName == "ICDPanels") {
          IcdPanelsFields?.panels?.forEach((panelsInfo: any) => {
            if (panelsInfo.panelName == parentPanelName) {
              panelsInfo.isVisible = false;
            }
          });
        }
      });

      // Uncheck the child checkbox
      inputsCopy[props.index].fields[props?.fieldIndex].panels[
        index
      ].testOptions[childIndex].isSelected = false;
      const currentPanel =
        inputsCopy[props.index].fields[props?.fieldIndex].panels[index];
      // Store the selected panel in storeChild if not already present
      setStoreChild((prevStoreChild: any) => {
        const panelIndex = prevStoreChild.findIndex(
          (panel: any) => panel.panelName === currentPanel.panelName
        );
        if (panelIndex !== -1) {
          // Update the existing panel with the recent one
          const updatedStoreChild = [...prevStoreChild];
          updatedStoreChild[panelIndex] = currentPanel;
          return updatedStoreChild;
        }
        // Add the new panel if it doesn't exist
        return [...prevStoreChild, currentPanel];
      });
      // Check if all child checkboxes in the parent panel are unchecked
      const allChildUnchecked = inputsCopy[props.index].fields[
        props?.fieldIndex
      ].panels[index].testOptions.every((option: any) => !option.isSelected);
      // If all child checkboxes are unchecked, update parent checkbox state and panel visibility
      if (allChildUnchecked) {
        // Uncheck the parent checkbox
        inputsCopy[props.index].fields[props?.fieldIndex].panels[
          index
        ].isSelected = false;
        if (specimenSourceIndex) {
          inputsCopy[specimenSourceIndex]?.fields?.forEach(
            (specimenSourceFields: any) => {
              specimenSourceFields?.specimenSources?.forEach(
                (panelsInfo: any) => {
                  if (panelsInfo?.panelName === parentPanelName) {
                    panelsInfo.isVisible = false;
                  }
                }
              );
            }
          );
        }
      }

      let newInputs = assignFormValues(
        inputsCopy,
        // props?.dependenceyControls,
        props?.index,
        props?.depControlIndex,
        props?.fieldIndex,
        storeChild,
        props?.isDependency,
        props?.repeatFieldSection,
        props?.isDependencyRepeatFields,
        props?.repeatFieldIndex,
        props?.repeatDependencySectionIndex,
        props?.repeatDepFieldIndex,
        undefined,
        props?.setInputs
      );
      newInputs?.then((res) => {
        let infectiousDataCopy = JSON?.parse(
          JSON?.stringify(props?.infectiousData)
        );
        infectiousDataCopy[
          FindIndex(props?.infectiousData, props?.ArrayReqId)
        ].sections = res;
        props?.setInfectiousData([...infectiousDataCopy]);
      });
    }
    // Update inputs and req states
  };

  const setpanelsArrToSendForEdit = () => {
    let panelsCopy = [...props?.panels];
    let filteredPanelsCopy = panelsCopy?.filter(
      (panelsData: any) => panelsData?.isSelected
    );
    let panelNameArr: any = [];
    filteredPanelsCopy?.forEach((panelData: any) => {
      panelNameArr?.push(panelData?.panelName);
    });
    let inputsCopy = [...props?.Inputs];
    assignFormValues(
      inputsCopy,
      // props?.dependenceyControls,
      props?.index,
      props?.depControlIndex,
      props?.fieldIndex,
      filteredPanelsCopy,
      props?.isDependency,
      props?.repeatFieldSection,
      props?.isDependencyRepeatFields,
      props?.repeatFieldIndex,
      props?.repeatDependencySectionIndex,
      props?.repeatDepFieldIndex,
      undefined,
      props?.setInputs
    );
    setPanelsArrToSend(filteredPanelsCopy);
  };
  const divElement = useRef<HTMLDivElement | null>(null); // Initialize ref for div
  useEffect(() => {
    // Scroll to the div if props.error is present
    if (props.error && divElement.current) {
      divElement.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
    props.setErrorFocussedInput && props.setErrorFocussedInput();
  }, [props?.errorFocussedInput]);
  return (
    <div ref={divElement}>
      {props.error && (
        <div className="form__error">
          <span>{t(props.error)}</span>
        </div>
      )}

      <div id={props?.name} ref={divElement} tabIndex={-1}></div>
      {/* Shown when a user tries to select a panel that is blocked by mutual exclusivity rules */}
      {mutualExclusivityError && (
        <div className="d-flex align-items-center gap-2 mb-3 text-muted" style={{ fontSize: "0.8rem" }}>
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, color: "#f59e0b" }}>
            <circle cx="8" cy="8" r="7.5" stroke="currentColor"/>
            <path d="M8 5v3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <circle cx="8" cy="11" r="0.75" fill="currentColor"/>
          </svg>
          <span style={{ color: "#92400e" }}>{mutualExclusivityError}</span>
        </div>
      )}
      {/* ArrayReqId 58: compact pill-only layout — hides test options, shows panel names only.
          Uses a custom SVG checkmark instead of a native checkbox for consistent styling. */}
      {props?.ArrayReqId === 58 ? (
        <div className="d-flex flex-column gap-2">
          {props?.panels?.map((options: any, index: number) => {
            if (!options.isVisible) return null;
            const isPanelDisabled = disabledPanelIds.has(String(options?.panelID));
            return (
              <label
                key={index}
                className={`d-flex align-items-center gap-2 px-4 py-2 border rounded ${
                  isPanelDisabled
                    ? "border-gray-200 text-muted"
                    : options?.isSelected
                    ? "border-primary bg-light-primary"
                    : "border-gray-300 bg-light"
                }`}
                style={{
                  cursor: isPanelDisabled ? "not-allowed" : "pointer",
                  opacity: isPanelDisabled ? 0.55 : 1,
                  userSelect: "none",
                  minWidth: "fit-content",
                }}
                onClick={(e: any) => {
                  if (isPanelDisabled) {
                    e.preventDefault();
                    const conflictingName = getConflictingPanelName(options?.panelID);
                    setMutualExclusivityError(
                      conflictingName
                        ? `"${options?.panelName}" cannot be selected because "${conflictingName}" is already selected. Only one panel can be selected from this group.`
                        : "Only one panel can be selected from this group. Please deselect the current selection first."
                    );
                  }
                }}
              >
                <input
                  type="checkbox"
                  className="d-none"
                  checked={options?.isSelected}
                  disabled={isPanelDisabled}
                  onChange={(e: any) => {
                    handleChangeParent(
                      options?.panelID,
                      options?.panelName,
                      e.target.checked,
                      index
                    );
                  }}
                />
                <span
                  className={`d-flex align-items-center justify-content-center rounded flex-shrink-0 ${
                    options?.isSelected ? "bg-primary border-primary" : "border border-gray-400 bg-white"
                  }`}
                  style={{ width: 18, height: 18 }}
                >
                  {options?.isSelected && (
                    <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
                      <path d="M1 4L4 7L10 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </span>
                <span className="fw-600">{options?.panelName}</span>
              </label>
            );
          })}
        </div>
      ) : (
        props?.panels?.map((options: any, index: number) => {
          const isPanelDisabled = disabledPanelIds.has(String(options?.panelID));
          return (
            <>
              {options.isVisible && (
                <div className={`${props?.displayType}`}>
                  <div className="row">
                    <div className="col-lg-12">
                      <div className={`card border ${isPanelDisabled ? "border-gray-200" : "border-gray-300"} mb-3 rounded`}
                        style={isPanelDisabled ? { opacity: 0.55 } : {}}>
                        <div
                          className="card-header bg-light d-flex justify-content-between align-items-center rounded min-h-35px px-4"
                          style={isPanelDisabled ? { cursor: "not-allowed" } : {}}
                          onClick={() => {
                            if (isPanelDisabled) {
                              const conflictingName = getConflictingPanelName(options?.panelID);
                              setMutualExclusivityError(
                                conflictingName
                                  ? `"${options?.panelName}" cannot be selected because "${conflictingName}" is already selected. Only one panel can be selected from this group.`
                                  : "Only one panel can be selected from this group. Please deselect the current selection first."
                              );
                            }
                          }}
                        >
                          <div className="col-12">
                            <label className="form-check form-check-sm form-check-solid col-12">
                              <input
                                className="form-check-input mr-2 h-20px w-20px"
                                type="checkbox"
                                checked={options?.isSelected}
                                disabled={isPanelDisabled}
                                onChange={(e: any) => {
                                  handleChangeParent(
                                    options?.panelID,
                                    options?.panelName,
                                    e.target.checked,
                                    index
                                  );
                                }}
                              />
                              {<span className="fw-600">{options?.panelName}</span>}
                            </label>
                          </div>
                        </div>
                        <div className="card-body py-md-4 py-3 col-12">
                          <div className="row">
                            {options?.testOptions?.map(
                              (optionsChild: any, childIndex: any) => (
                                <>
                                  <div
                                    className={
                                      options?.testOptions.length > 1
                                        ? "mb-3 col-xxl-3 col-xl-4 col-lg-4 col-md-6 col-sm-12"
                                        : "mb-3 col-xl-12 col-lg-12 col-md-12 col-sm-12"
                                    }
                                  >
                                    <label className="form-check form-check-sm align-items-start form-check-solid col-12">
                                      <input
                                        className="form-check-input mr-2 h-20px w-20px"
                                        type="checkbox"
                                        disabled={isPanelDisabled}
                                        onChange={(e: any) => {
                                          handleChangeChild(
                                            optionsChild?.testID,
                                            e?.target?.checked,
                                            options?.panelName,
                                            index,
                                            childIndex,
                                            options?.panelID
                                          );
                                        }}
                                        checked={optionsChild?.isSelected}
                                      />
                                      <div className="d-flex justify-content-between">
                                        <span className="fw-400">
                                          {optionsChild?.testName}
                                        </span>
                                        <span className="text-muted">
                                          {optionsChild?.specimenType}
                                        </span>
                                      </div>
                                    </label>
                                  </div>
                                </>
                              )
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          );
        })
      )}
    </div>
  );
};

export { MultipleHeaderSelectable };
